import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { toast } from 'sonner';

import { UploadService } from './upload-service';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    videoUpload: {
      uploadVideos: (files: File[]) => ReturnType;
      insertVideo: (src: string, alt?: string) => ReturnType;
    };
  }
}

interface VideoUploadOptions {
  acceptedTypes: string[];
  HTMLAttributes: Record<string, any>;
  owner: string;
  repo: string;
}

// Store active video uploads
const activeVideoUploads = new Map<string, { file: File; progress: number; placeholder?: HTMLElement }>();

// Create a loading placeholder for video uploads
function createVideoLoadingPlaceholder(file: File, uploadId: string): HTMLElement {
  const container = document.createElement('div');
  container.className = 'video-upload-placeholder';
  container.setAttribute('data-upload-id', uploadId);
  container.style.cssText = `
    display: block;
    position: relative;
    width: 400px;
    height: 225px;
    background: #f6f8fa;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    margin: 8px 0;
    overflow: hidden;
  `;

  // Create video icon background
  const videoIcon = document.createElement('div');
  videoIcon.style.cssText = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background: #8b949e;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.3;
  `;

  // Simple play icon using CSS
  const playIcon = document.createElement('div');
  playIcon.style.cssText = `
    width: 0;
    height: 0;
    border-left: 20px solid white;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;
  `;

  videoIcon.appendChild(playIcon);
  container.appendChild(videoIcon);

  // Create overlay with spinner and progress
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  `;

  // Create spinner
  const spinner = document.createElement('div');
  spinner.className = 'upload-spinner';
  spinner.style.cssText = `
    width: 24px;
    height: 24px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  `;

  const progressText = document.createElement('div');
  progressText.className = 'upload-progress';
  progressText.textContent = 'Uploading video...';
  progressText.style.cssText = `
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 14px;
  `;

  const fileName = document.createElement('div');
  fileName.textContent = file.name;
  fileName.style.cssText = `
    font-size: 12px;
    opacity: 0.9;
    text-align: center;
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;

  overlay.appendChild(spinner);
  overlay.appendChild(progressText);
  overlay.appendChild(fileName);
  container.appendChild(overlay);

  return container;
}

// Add CSS for spinner animation
function addSpinnerStyles() {
  if (!document.getElementById('upload-spinner-styles')) {
    const style = document.createElement('style');
    style.id = 'upload-spinner-styles';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .video-upload-placeholder {
        user-select: none;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
}

export const VideoUpload = Node.create<VideoUploadOptions>({
  name: 'video',

  group: 'block',

  addOptions() {
    return {
      acceptedTypes: ['video/mp4', 'video/webm', 'video/ogg'],
      HTMLAttributes: {
        controls: true,
        preload: 'metadata',
        style: 'max-width: 100%; max-height: 500px; height: auto;',
      },
      owner: '',
      repo: '',
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {};
          }
          return { src: attributes.src };
        },
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute('alt'),
        renderHTML: (attributes) => {
          if (!attributes.alt) {
            return {};
          }
          return { alt: attributes.alt };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addCommands() {
    return {
      insertVideo:
        (src: string, alt?: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { src, alt },
          });
        },

      uploadVideos:
        (files: File[]) =>
        ({ editor }) => {
          // Add spinner styles
          addSpinnerStyles();

          // Process files sequentially to avoid race conditions
          const processFiles = async () => {
            for (const file of files) {
              const uploadId = `video-upload-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

              try {
                // Get current cursor position
                const { from } = editor.state.selection;

                // Create and insert placeholder
                const placeholder = createVideoLoadingPlaceholder(file, uploadId);
                activeVideoUploads.set(uploadId, { file, progress: 0, placeholder });

                // Insert a temporary node that we'll replace
                const tempNode = editor.schema.text(`[Uploading video ${file.name}...]`);
                const tr = editor.state.tr.insert(from, tempNode);
                editor.view.dispatch(tr);

                // Get owner/repo from extension options (required)
                const { owner, repo } = this.options;
                if (!owner || !repo) {
                  throw new Error('VideoUpload extension requires owner and repo options to be configured');
                }

                // Use the upload service with progress tracking
                const result = await UploadService.uploadFile(file, owner, repo, (progress) => {
                  // Update progress in the active uploads
                  const upload = activeVideoUploads.get(uploadId);
                  if (upload) {
                    upload.progress = progress.percentage;

                    // Update the placeholder progress text
                    const progressElement = upload.placeholder?.querySelector('.upload-progress');
                    if (progressElement) {
                      progressElement.textContent = `${progress.percentage}%`;
                    }
                  }
                });

                // Find and replace the temporary text with actual video
                const currentState = editor.state;
                const doc = currentState.doc;
                const tempText = `[Uploading video ${file.name}...]`;

                // Find the position of our temporary text
                let found = false;
                doc.descendants((node, pos) => {
                  if (found) return false;
                  if (node.isText && node.text?.includes(tempText)) {
                    const start = pos + (node.text.indexOf(tempText) || 0);
                    const end = start + tempText.length;

                    // Replace with actual video
                    editor
                      .chain()
                      .focus()
                      .deleteRange({ from: start, to: end })
                      .insertVideo(result.url, file.name)
                      .run();

                    found = true;
                    return false;
                  }
                });

                // Clean up
                activeVideoUploads.delete(uploadId);

                // Success toast removed - only show errors
              } catch (error) {
                console.error('Video upload failed:', error);

                // Clean up failed upload
                activeVideoUploads.delete(uploadId);

                // Remove temporary text if upload failed
                const currentState = editor.state;
                const doc = currentState.doc;
                const tempText = `[Uploading video ${file.name}...]`;

                doc.descendants((node, pos) => {
                  if (node.isText && node.text?.includes(tempText)) {
                    const start = pos + (node.text.indexOf(tempText) || 0);
                    const end = start + tempText.length;

                    editor.chain().focus().deleteRange({ from: start, to: end }).run();

                    return false;
                  }
                });

                toast.error(
                  `Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
                );
              }
            }
          };

          // Execute the async function
          processFiles();

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const options = this.options;

    return [
      new Plugin({
        key: new PluginKey('videoUpload'),
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, set) {
            // Keep decorations in sync with document changes
            set = set.map(tr.mapping, tr.doc);

            // Add decorations for video upload placeholders
            const decorations: Decoration[] = [];

            tr.doc.descendants((node, pos) => {
              if (node.isText && node.text) {
                // Find video upload placeholder text
                const uploadMatches = node.text.match(/\[Uploading video (.+?)\.\.\.\]/g);
                if (uploadMatches) {
                  uploadMatches.forEach((match) => {
                    const fileName = match.match(/\[Uploading video (.+?)\.\.\.\]/)?.[1];
                    if (fileName) {
                      // Find corresponding upload
                      for (const [uploadId, upload] of activeVideoUploads.entries()) {
                        if (upload.file.name === fileName && upload.placeholder) {
                          const start = pos + (node.text!.indexOf(match) || 0);
                          const end = start + match.length;

                          decorations.push(
                            Decoration.widget(start, upload.placeholder, {
                              side: 0,
                              key: uploadId,
                            })
                          );
                          break;
                        }
                      }
                    }
                  });
                }
              }
            });

            if (decorations.length > 0) {
              set = set.add(tr.doc, decorations);
            }

            return set;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleDOMEvents: {
            drop(view, event) {
              const hasFiles = event.dataTransfer?.files && event.dataTransfer.files.length > 0;
              if (!hasFiles) return false;

              const files = Array.from(event.dataTransfer.files).filter((file) =>
                options.acceptedTypes.includes(file.type)
              );

              if (files.length === 0) return false;

              event.preventDefault();

              editor.commands.uploadVideos(files);
              return true;
            },

            paste(view, event) {
              const hasFiles = event.clipboardData?.files && event.clipboardData.files.length > 0;
              if (!hasFiles) return false;

              const files = Array.from(event.clipboardData.files).filter((file) =>
                options.acceptedTypes.includes(file.type)
              );

              if (files.length === 0) return false;

              event.preventDefault();

              editor.commands.uploadVideos(files);
              return true;
            },
          },
        },
      }),

      // Cleanup plugin for detecting removed videos
      new Plugin({
        key: new PluginKey('videoCleanup'),
        appendTransaction(transactions, oldState, newState) {
          // Track videos that were removed
          const oldVideos = new Set<string>();
          const newVideos = new Set<string>();

          // Collect all video URLs from old state
          oldState.doc.descendants((node) => {
            if (node.type.name === 'video' && node.attrs.src) {
              oldVideos.add(node.attrs.src);
            }
          });

          // Collect all video URLs from new state
          newState.doc.descendants((node) => {
            if (node.type.name === 'video' && node.attrs.src) {
              newVideos.add(node.attrs.src);
            }
          });

          // Find removed videos (in old but not in new)
          const removedVideos = Array.from(oldVideos).filter((url) => !newVideos.has(url));

          // Delete removed videos from S3 (only our own uploads)
          if (removedVideos.length > 0) {
            removedVideos.forEach(async (url) => {
              try {
                // Only delete if it's from our S3 bucket
                if (url.includes('uploads/')) {
                  await UploadService.deleteFile(url);
                }
              } catch (error) {
                console.warn('Failed to delete removed video:', url, error);
              }
            });
          }

          return null;
        },
      }),
    ];
  },
});
